/**
 * Parser utilities for JSON, XML, and CSV
 *
 * Handles auto-detection and parsing of different data formats
 */

import { XMLParser } from 'fast-xml-parser';
import Papa from 'papaparse';

export const DataFormat = {
  JSON: 'json',
  XML: 'xml',
  CSV: 'csv',
  YAML: 'yaml',
  TOML: 'toml',
  INI: 'ini',
  ENV: 'env',
  PROPERTIES: 'properties',
  TEXT: 'text',
  UNKNOWN: 'unknown'
};

/**
 * Auto-detect format based on content
 */
export function detectFormat(text) {
  if (!text || typeof text !== 'string') {
    return DataFormat.UNKNOWN;
  }

  const trimmed = text.trim();

  // Check for JSON
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      JSON.parse(trimmed);
      return DataFormat.JSON;
    } catch {
      // Not valid JSON, continue checking
    }
  }

  // Check for XML
  if (trimmed.startsWith('<') && trimmed.includes('>')) {
    // Simple heuristic: if it has XML tags
    if (trimmed.match(/<[^>]+>/)) {
      return DataFormat.XML;
    }
  }

  // Check for CSV
  // Look for comma-separated values with consistent structure
  const lines = trimmed.split('\n').filter(line => line.trim());
  if (lines.length >= 2) {
    const firstLineCommas = (lines[0].match(/,/g) || []).length;
    const secondLineCommas = (lines[1].match(/,/g) || []).length;

    // If multiple lines have similar comma counts, likely CSV
    if (firstLineCommas > 0 && firstLineCommas === secondLineCommas) {
      return DataFormat.CSV;
    }
  }

  return DataFormat.UNKNOWN;
}

/**
 * Parse JSON with detailed error reporting
 */
export function parseJSON(text) {
  try {
    const parsed = JSON.parse(text);
    return {
      success: true,
      data: parsed,
      error: null
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: `JSON Parse Error: ${error.message}`
    };
  }
}

/**
 * Parse XML to JavaScript object
 */
export function parseXML(text) {
  try {
    const parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: '@_',
      textNodeName: '#text',
      parseAttributeValue: true,
      parseTagValue: true,
      trimValues: true
    });

    const parsed = parser.parse(text);
    return {
      success: true,
      data: parsed,
      error: null
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: `XML Parse Error: ${error.message}`
    };
  }
}

/**
 * Parse CSV to array of objects
 *
 * @param {string} text - CSV text
 * @param {string} primaryKey - Column to use as primary key for row comparison
 * @returns {Object} Parse result
 */
export function parseCSV(text, primaryKey = null) {
  try {
    const result = Papa.parse(text, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim()
    });

    if (result.errors.length > 0) {
      const errorMessages = result.errors
        .map(e => `Row ${e.row}: ${e.message}`)
        .join('; ');

      return {
        success: false,
        data: null,
        error: `CSV Parse Error: ${errorMessages}`
      };
    }

    // Convert to object format for diffing
    let data = result.data;

    // If primary key specified, convert to keyed object
    if (primaryKey && data.length > 0 && data[0][primaryKey] !== undefined) {
      const keyedData = {};
      data.forEach((row, idx) => {
        const key = row[primaryKey];
        if (key !== null && key !== undefined) {
          keyedData[key] = row;
        } else {
          // Fallback to index if key is missing
          keyedData[`_row_${idx}`] = row;
        }
      });
      data = keyedData;
    }

    return {
      success: true,
      data,
      error: null,
      headers: result.meta.fields
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: `CSV Parse Error: ${error.message}`
    };
  }
}

/**
 * Parse YAML-like text to object (simplified parser for diff purposes)
 */
export function parseYAMLLike(text) {
  try {
    // Convert YAML to JSON-like structure for comparison
    const lines = text.split('\n');
    const result = {};
    let currentPath = [];
    let currentIndent = 0;

    lines.forEach(line => {
      const trimmed = line.trimEnd();
      if (!trimmed || trimmed.startsWith('#')) return;

      const indent = trimmed.length - trimmed.trimStart().length;
      const content = trimmed.trim();

      if (content.includes(':')) {
        const [key, ...valueParts] = content.split(':');
        const value = valueParts.join(':').trim();

        // Store as key-value for diffing
        const fullKey = `${' '.repeat(indent)}${key.trim()}`;
        result[fullKey] = value || '(object)';
      } else if (content.startsWith('-')) {
        // Array item
        const item = content.substring(1).trim();
        const fullKey = `${' '.repeat(indent)}- ${item}`;
        result[fullKey] = true;
      } else {
        result[trimmed] = true;
      }
    });

    return {
      success: true,
      data: result,
      error: null
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: `YAML Parse Error: ${error.message}`
    };
  }
}

/**
 * Parse INI/TOML/Properties format
 */
export function parseINILike(text) {
  try {
    const lines = text.split('\n');
    const result = {};
    let currentSection = 'root';

    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith(';')) return;

      // Section header [section]
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        currentSection = trimmed.slice(1, -1);
        result[`[${currentSection}]`] = '(section)';
        return;
      }

      // Key-value pair
      const separators = ['=', ':'];
      for (const sep of separators) {
        if (trimmed.includes(sep)) {
          const [key, ...valueParts] = trimmed.split(sep);
          const value = valueParts.join(sep).trim();
          const fullKey = currentSection !== 'root' ? `${currentSection}.${key.trim()}` : key.trim();
          result[fullKey] = value;
          return;
        }
      }

      // Standalone line
      result[trimmed] = true;
    });

    return {
      success: true,
      data: result,
      error: null
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: `INI Parse Error: ${error.message}`
    };
  }
}

/**
 * Parse ENV file format
 */
export function parseENV(text) {
  try {
    const lines = text.split('\n');
    const result = {};

    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;

      if (trimmed.includes('=')) {
        const [key, ...valueParts] = trimmed.split('=');
        let value = valueParts.join('=').trim();

        // Remove quotes if present
        if ((value.startsWith('"') && value.endsWith('"')) ||
            (value.startsWith("'") && value.endsWith("'"))) {
          value = value.slice(1, -1);
        }

        result[key.trim()] = value;
      }
    });

    return {
      success: true,
      data: result,
      error: null
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: `ENV Parse Error: ${error.message}`
    };
  }
}

/**
 * Parse text as line-by-line object for diffing
 */
export function parseAsText(text) {
  try {
    const lines = text.split('\n');
    const result = {};

    lines.forEach((line, index) => {
      const key = `Line ${index + 1}`;
      result[key] = line;
    });

    return {
      success: true,
      data: result,
      error: null
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: `Text Parse Error: ${error.message}`
    };
  }
}

/**
 * Universal parser - auto-detects format and parses
 *
 * @param {string} text - Input text
 * @param {string} format - Force specific format (optional)
 * @param {Object} options - Parser options (e.g., CSV primary key)
 * @returns {Object} Parse result with format and data
 */
export function parseData(text, format = null, options = {}) {
  if (!text || text.trim() === '') {
    return {
      success: false,
      data: null,
      format: DataFormat.UNKNOWN,
      error: 'Empty input'
    };
  }

  // Auto-detect if format not specified
  const detectedFormat = format || detectFormat(text);

  let result;
  let actualFormat = detectedFormat;

  // Map format aliases to base parsers
  const formatMap = {
    // JSON-based formats
    'json': 'json',
    'tsconfig': 'json',
    'eslint': 'json',
    'prettier': 'json',
    'jest': 'json',
    'babel': 'json',
    'composer': 'json',
    'vercel': 'json',
    'arm': 'json',
    'grafana': 'json',
    'api': 'json',
    'package': 'json',

    // XML-based formats
    'xml': 'xml',
    'maven': 'xml',
    'nuget': 'xml',

    // YAML-based formats
    'yaml': 'yaml',
    'kubernetes': 'yaml',
    'docker-compose': 'yaml',
    'ansible': 'yaml',
    'helm': 'yaml',
    'github': 'yaml',
    'gitlab': 'yaml',
    'circleci': 'yaml',
    'travis': 'yaml',
    'appveyor': 'yaml',
    'bitbucket': 'yaml',
    'cloudformation': 'yaml',
    'conda': 'yaml',
    'prometheus': 'yaml',

    // INI/TOML/Properties formats
    'toml': 'ini',
    'ini': 'ini',
    'properties': 'ini',
    'cargo': 'ini',
    'poetry': 'ini',
    'pipfile': 'ini',
    'netlify': 'ini',
    'editorconfig': 'ini',

    // ENV format
    'env': 'env',

    // CSV format
    'csv': 'csv',

    // Text-based (everything else)
    'dockerfile': 'text',
    'terraform': 'text',
    'nginx': 'text',
    'makefile': 'text',
    'cmake': 'text',
    'gitignore': 'text',
    'graphql': 'text',
    'sql': 'text',
    'python': 'text',
    'javascript': 'text',
    'typescript': 'text',
    'gradle': 'text',
    'gomod': 'text',
    'ruby': 'text',
    'txt': 'text'
  };

  const baseFormat = formatMap[detectedFormat] || detectedFormat;

  switch (baseFormat) {
    case 'json':
      result = parseJSON(text);
      break;

    case 'xml':
      result = parseXML(text);
      break;

    case 'csv':
      result = parseCSV(text, options.csvPrimaryKey);
      break;

    case 'yaml':
      result = parseYAMLLike(text);
      break;

    case 'ini':
      result = parseINILike(text);
      break;

    case 'env':
      result = parseENV(text);
      break;

    case 'text':
      result = parseAsText(text);
      break;

    default:
      // Try JSON first, then XML, then YAML, then text
      result = parseJSON(text);
      if (!result.success) {
        result = parseXML(text);
      }
      if (!result.success) {
        result = parseYAMLLike(text);
      }
      if (!result.success) {
        result = parseAsText(text);
      }
      break;
  }

  return {
    ...result,
    format: detectedFormat
  };
}

/**
 * Extract CSV headers from text
 */
export function extractCSVHeaders(text) {
  try {
    const result = Papa.parse(text, {
      header: true,
      preview: 1
    });

    return result.meta.fields || [];
  } catch {
    return [];
  }
}
